import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useTheme } from './useTheme';
import { useScreen } from './useScreen';
import { getSalesConfig, CHANNEL_COLORS, CHANNEL_LABELS, getChannelLabel } from './salesConfig';

const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', logoGrad:'linear-gradient(135deg, #534AB7, #1D9E75)', triGrad:'linear-gradient(135deg, #5dc1b9, #FDBE02, #eb636b)' };
const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";

function Bar() { return <div style={{ width:4, height:24, borderRadius:2, background:C.triGrad, flexShrink:0 }}/>; }

export default function SalesModule({ userId, profile }) {
  const t = useTheme();
  const { isMobile } = useScreen();
  const salesType = profile?.sales_type || 'productos';
  const sc = getSalesConfig(salesType);

  const [product, setProduct] = useState('');
  const [price, setPrice] = useState('');
  const [channel, setChannel] = useState('store');
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [rentalDate, setRentalDate] = useState('');
  const [ticketQty, setTicketQty] = useState('1');
  const [eventName, setEventName] = useState('');
  const [isNew, setIsNew] = useState(true);
  const [sales, setSales] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [receiptSale, setReceiptSale] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState('paid'); // 'paid' or 'layaway'
  const [deposit, setDeposit] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!userId) return;
    supabase.from('daily_sales').select('*').eq('user_id', userId).order('created_at', { ascending:false }).limit(50).then(({ data }) => {
      if (data) setSales(data);
    });
  }, [userId, refresh]);

  const todaySales = sales.filter(s => s.sale_date === new Date().toISOString().split('T')[0]);
  const todayTotal = todaySales.reduce((s, v) => s + (v.price || 0), 0);

  // Channel grouping for dona
  const digitalChannels = ['redes', 'whatsapp', 'web', 'online'];
  const tradChannels = ['local', 'referido', 'store'];
  const digitalTotal = sales.reduce((s, v) => s + (digitalChannels.includes(v.channel) ? (v.price || 0) : 0), 0);
  const tradTotal = sales.reduce((s, v) => s + (tradChannels.includes(v.channel) ? (v.price || 0) : 0), 0);
  const allTotal = digitalTotal + tradTotal;
  const digitalPct = allTotal > 0 ? Math.round((digitalTotal / allTotal) * 100) : 0;

  // Per-channel breakdown
  const channelTotals = {};
  sales.forEach(s => { channelTotals[s.channel] = (channelTotals[s.channel] || 0) + (s.price || 0); });

  // Top products
  const prodMap = {};
  sales.forEach(s => {
    const k = (s.product_name || '').toLowerCase().trim();
    if (!k) return;
    if (!prodMap[k]) prodMap[k] = { name: s.product_name, count: 0, total: 0 };
    prodMap[k].count++;
    prodMap[k].total += s.price || 0;
  });
  const topProducts = Object.values(prodMap).sort((a, b) => b.count - a.count).slice(0, 5);

  const generateFolio = () => {
    const d = saleDate.replace(/-/g, '');
    const count = todaySales.length + 1;
    return `TD-${d}-${String(count).padStart(4, '0')}`;
  };

  const registerSale = async () => {
    if (!product.trim() || !price) {
      setConfirm('⚠️ Falta producto o precio');
      setTimeout(() => setConfirm(null), 3000);
      return;
    }
    if (!userId) {
      setConfirm('⚠️ Error: usuario no identificado');
      setTimeout(() => setConfirm(null), 3000);
      return;
    }
    if (paymentStatus === 'layaway' && (!deposit || Number(deposit) <= 0)) {
      setConfirm('⚠️ Falta el monto del anticipo');
      setTimeout(() => setConfirm(null), 3000);
      return;
    }
    if (paymentStatus === 'layaway' && Number(deposit) >= Number(price)) {
      setConfirm('⚠️ El anticipo no puede ser mayor o igual al total');
      setTimeout(() => setConfirm(null), 3000);
      return;
    }
    if (paymentStatus === 'layaway' && !customerName.trim()) {
      setConfirm('⚠️ El nombre del cliente es obligatorio para apartados');
      setTimeout(() => setConfirm(null), 3000);
      return;
    }
    const folio = generateFolio();
    const now = new Date();
    const row = {
      user_id: userId, product_name: product, price: Number(price), channel,
      folio, sale_date: saleDate,
    };
    // Optional columns — only include if they have values
    if (paymentStatus && paymentStatus !== 'paid') row.payment_status = paymentStatus;
    if (paymentStatus === 'layaway' && deposit) row.deposit_amount = Number(deposit);
    if (paymentStatus === 'layaway' && customerName.trim()) row.customer_name = customerName.trim();
    if (salesType === 'boletos' && isNew !== null) row.is_new_customer = isNew;
    if (salesType === 'rentas' && rentalDate) row.rental_date = rentalDate;
    if (salesType === 'boletos' && ticketQty) row.ticket_quantity = Number(ticketQty);
    if (salesType === 'boletos' && eventName) row.event_name = eventName;
    console.log('[SalesModule] Inserting sale:', JSON.stringify(row));
    const { data, error } = await supabase.from('daily_sales').insert(row).select();
    if (error) {
      console.error('[SalesModule] Insert error:', error);
      setConfirm(`❌ Error: ${error.message}`);
      setTimeout(() => setConfirm(null), 5000);
      return;
    }
    console.log('[SalesModule] Sale inserted:', data);
    setReceiptSale({ ...row, time: now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) });
    setConfirm(`✓ ${folio} — ${product} $${Number(price).toLocaleString()}`);
    setProduct(''); setPrice(''); setTicketQty('1'); setEventName(''); setRentalDate('');
    setDeposit(''); setCustomerName(''); setPaymentStatus('paid');
    setRefresh(r => r + 1);
    setTimeout(() => setConfirm(null), 3000);
  };

  const completeLayaway = async (sale) => {
    const newFolio = `${sale.folio}-F`;
    console.log('[SalesModule] Completing layaway:', sale.id, 'folio:', newFolio);
    const { error } = await supabase.from('daily_sales').update({
      payment_status: 'paid',
      folio: newFolio,
    }).eq('id', sale.id);
    if (error) {
      console.error('[SalesModule] Complete layaway error:', error.message, error.code, error.details);
      setConfirm(`❌ Error al completar pago: ${error.message}`);
      setTimeout(() => setConfirm(null), 5000);
      return;
    }
    const now = new Date();
    setReceiptSale({
      ...sale,
      folio: newFolio,
      original_folio: sale.folio,
      payment_status: 'paid',
      final_payment: (sale.price || 0) - (sale.deposit_amount || 0),
      time: now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),
      is_completion: true,
    });
    setRefresh(r => r + 1);
  };

  const deleteSale = async (id) => {
    await supabase.from('daily_sales').delete().eq('id', id);
    setRefresh(r => r + 1);
  };

  // Pending layaways
  const pendingLayaways = sales.filter(s => s.payment_status === 'layaway');

  // Search
  const searchLower = search.toLowerCase().trim();
  const searchResults = searchLower
    ? sales.filter(s => (s.folio || '').toLowerCase().includes(searchLower) || (s.product_name || '').toLowerCase().includes(searchLower) || (s.customer_name || '').toLowerCase().includes(searchLower))
    : [];

  const exportCSV = () => {
    const rows = [['Fecha', 'Producto', 'Precio', 'Canal', 'Folio']];
    sales.forEach(s => rows.push([s.sale_date, s.product_name, s.price, getChannelLabel(s.channel, salesType), s.folio || '']));
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'ventas_td.csv'; a.click();
  };

  const printReceipt = () => {
    const style = document.createElement('style');
    style.id = 'td-print';
    style.textContent = `@media print{body *{visibility:hidden!important}#td-receipt,#td-receipt *{visibility:visible!important}#td-receipt{position:fixed!important;left:0!important;top:0!important;width:80mm!important;padding:4mm!important;font-size:11px!important;color:#000!important;background:#fff!important}}`;
    document.head.appendChild(style); window.print(); setTimeout(() => style.remove(), 500);
  };

  const downloadPDF = async () => {
    const el = document.getElementById('td-receipt');
    if (!el || !window.html2canvas || !window.jspdf) return;
    const canvas = await window.html2canvas(el, { scale: 2, backgroundColor: '#fff' });
    const h = canvas.height * 80 / canvas.width;
    const pdf = new window.jspdf.jsPDF({ unit: 'mm', format: [80, h] });
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, 80, h);
    pdf.save('ticket-' + (receiptSale?.folio || 'ticket') + '.pdf');
  };

  const shareTicket = async () => {
    const el = document.getElementById('td-receipt');
    if (!el || !window.html2canvas) return;
    const canvas = await window.html2canvas(el, { scale: 2, backgroundColor: '#fff' });
    canvas.toBlob(async (blob) => {
      const file = new File([blob], 'ticket-' + (receiptSale?.folio || '') + '.png', { type: 'image/png' });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: 'Ticket', text: 'Gracias por tu compra en ' + (profile?.business_name || 'T&D'), files: [file] });
      } else {
        const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = file.name; a.click();
      }
    }, 'image/png');
  };

  const inp = { width: '100%', padding: '10px 14px', border: `0.5px solid ${t.border}`, borderRadius: 12, fontSize: 14, fontFamily: font, background: t.card, color: t.text, outline: 'none', boxSizing: 'border-box' };
  const unitLabel = sc.unitLabel;

  return (
    <section style={{ marginBottom: '2.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}><Bar /><h2 style={{ fontFamily: serif, fontSize: isMobile ? 18 : 22, fontWeight: 400, margin: 0 }}>Control de {unitLabel.toLowerCase()}</h2></div>

      {/* Register form */}
      <div style={{ background: t.card, border: `0.5px solid ${t.border}`, borderRadius: 20, padding: isMobile ? '1.25rem' : '1.5rem', marginBottom: 16 }}>
        {confirm && <div style={{ background: C.turquesa + '15', border: `0.5px solid ${C.turquesa}40`, borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: C.turquesa, fontWeight: 500 }}>{confirm}</div>}
        <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 12px' }}>Registrar {unitLabel.toLowerCase()}</p>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 12 }}>
          {salesType === 'boletos' && (
            <div>
              <label style={{ fontSize: 12, color: t.textSecondary, display: 'block', marginBottom: 4 }}>¿Qué evento?</label>
              <input value={eventName} onChange={e => setEventName(e.target.value)} placeholder="Ej: Carrera 5K" style={inp} />
            </div>
          )}
          <div>
            <label style={{ fontSize: 12, color: t.textSecondary, display: 'block', marginBottom: 4 }}>{sc.productLabel}</label>
            <input value={product} onChange={e => setProduct(e.target.value)} placeholder={sc.productPlaceholder} style={inp} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: t.textSecondary, display: 'block', marginBottom: 4 }}>Precio $</label>
            <div style={{ display: 'flex', border: `0.5px solid ${t.border}`, borderRadius: 12, overflow: 'hidden' }}>
              <span style={{ padding: '10px 12px', background: t.isDark ? '#333' : '#f0f0ed', fontSize: 14, fontWeight: 500 }}>$</span>
              <input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="0" style={{ flex: 1, padding: '10px 12px', border: 'none', outline: 'none', fontSize: 14, fontFamily: font, background: t.card, color: t.text }} />
            </div>
          </div>
          {salesType === 'boletos' && (
            <div>
              <label style={{ fontSize: 12, color: t.textSecondary, display: 'block', marginBottom: 4 }}>Cantidad de boletos</label>
              <input type="number" value={ticketQty} onChange={e => setTicketQty(e.target.value)} style={inp} />
            </div>
          )}
          {salesType === 'rentas' && (
            <div>
              <label style={{ fontSize: 12, color: t.textSecondary, display: 'block', marginBottom: 4 }}>Fecha de renta</label>
              <input type="date" value={rentalDate} onChange={e => setRentalDate(e.target.value)} style={inp} />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {sc.channels.map(ch => (
            <button key={ch} onClick={() => setChannel(ch)} style={{ padding: '7px 12px', fontSize: 12, fontFamily: font, cursor: 'pointer', borderRadius: 50, border: `0.5px solid ${channel === ch ? (CHANNEL_COLORS[ch] || C.turquesa) : t.border}`, background: channel === ch ? (CHANNEL_COLORS[ch] || C.turquesa) : t.card, color: channel === ch ? '#fff' : t.text, fontWeight: 500 }}>{getChannelLabel(ch, salesType)}</button>
          ))}
          {salesType === 'boletos' && (
            <button onClick={() => setIsNew(!isNew)} style={{ padding: '7px 12px', fontSize: 12, fontFamily: font, cursor: 'pointer', borderRadius: 50, border: `0.5px solid ${isNew ? C.turquesa : C.mango}`, background: (isNew ? C.turquesa : C.mango) + '15', color: isNew ? C.turquesa : C.mango, fontWeight: 500 }}>{isNew ? '🆕 Nuevo' : '🔄 Repetido'}</button>
          )}
        </div>

        {/* Payment status */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <button onClick={() => setPaymentStatus('paid')} style={{ padding: '8px 16px', fontSize: 13, fontFamily: font, cursor: 'pointer', borderRadius: 50, border: `1.5px solid ${paymentStatus === 'paid' ? C.turquesa : t.border}`, background: paymentStatus === 'paid' ? C.turquesa + '15' : t.card, color: paymentStatus === 'paid' ? C.turquesa : t.text, fontWeight: 600 }}>💰 Pagado</button>
          <button onClick={() => setPaymentStatus('layaway')} style={{ padding: '8px 16px', fontSize: 13, fontFamily: font, cursor: 'pointer', borderRadius: 50, border: `1.5px solid ${paymentStatus === 'layaway' ? C.mango : t.border}`, background: paymentStatus === 'layaway' ? C.mango + '15' : t.card, color: paymentStatus === 'layaway' ? C.mango : t.text, fontWeight: 600 }}>🔒 Apartado</button>
        </div>

        {/* Layaway extra fields */}
        {paymentStatus === 'layaway' && (
          <div style={{ background: C.mango + '10', border: `0.5px solid ${C.mango}30`, borderRadius: 12, padding: 12, marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: t.textSecondary, display: 'block', marginBottom: 4 }}>Nombre del cliente *</label>
            <input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Ej: Pedro García" style={{ ...inp, marginBottom: 10 }} />
            <label style={{ fontSize: 12, color: t.textSecondary, display: 'block', marginBottom: 4 }}>¿Cuánto dejó de anticipo? $</label>
            <div style={{ display: 'flex', border: `0.5px solid ${t.border}`, borderRadius: 12, overflow: 'hidden', background: t.card }}>
              <span style={{ padding: '10px 12px', background: t.isDark ? '#333' : '#f0f0ed', fontSize: 14, fontWeight: 500 }}>$</span>
              <input type="number" value={deposit} onChange={e => setDeposit(e.target.value)} placeholder="0" style={{ flex: 1, padding: '10px 12px', border: 'none', outline: 'none', fontSize: 14, fontFamily: font, background: t.card, color: t.text }} />
            </div>
            {price && deposit && Number(deposit) > 0 && Number(deposit) < Number(price) && (
              <p style={{ fontSize: 13, color: C.mango, fontWeight: 600, margin: '8px 0 0' }}>Resta por pagar: ${(Number(price) - Number(deposit)).toLocaleString()}</p>
            )}
          </div>
        )}

        <button onClick={registerSale} style={{ background: C.logoGrad, color: '#fff', border: 'none', borderRadius: 50, padding: '10px 28px', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: font, width: isMobile ? '100%' : 'auto' }}>Registrar</button>
      </div>

      {/* Search */}
      <div style={{ background: t.card, border: `0.5px solid ${t.border}`, borderRadius: 20, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16 }}>🔍</span>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por folio o producto..." style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, fontFamily: font, background: 'transparent', color: t.text }} />
        {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: t.textSecondary, fontSize: 16, cursor: 'pointer', padding: 0 }}>×</button>}
      </div>

      {/* Search results */}
      {search && (
        <div style={{ background: t.card, border: `0.5px solid ${t.border}`, borderRadius: 20, padding: isMobile ? '1.25rem' : '1.5rem', marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: t.textSecondary, margin: '0 0 12px' }}>{searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''}</p>
          {searchResults.length === 0 ? (
            <p style={{ fontSize: 13, color: t.textSecondary, textAlign: 'center', padding: 12 }}>Sin resultados</p>
          ) : (
            searchResults.map(s => (
              <div key={s.id} onClick={() => setReceiptSale({ ...s, time: '' })} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: `0.5px solid ${t.border}`, cursor: 'pointer' }}>
                <span style={{ fontSize: 12 }}>{s.payment_status === 'layaway' ? '🔒' : '💰'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, margin: 0, fontWeight: 500 }}>{s.customer_name ? `${s.customer_name} — ` : ''}{s.product_name}</p>
                  <p style={{ fontSize: 10, color: t.textSecondary, margin: 0 }}>{s.folio} · {s.sale_date}</p>
                </div>
                <span style={{ fontSize: 13, fontWeight: 600 }}>${(s.price || 0).toLocaleString()}</span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pending layaways */}
      {pendingLayaways.length > 0 && (
        <div style={{ background: C.mango + '08', border: `1px solid ${C.mango}30`, borderRadius: 20, padding: isMobile ? '1.25rem' : '1.5rem', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 18 }}>🔒</span>
            <p style={{ fontSize: 14, fontWeight: 600, margin: 0, color: C.mango }}>Apartados pendientes ({pendingLayaways.length})</p>
          </div>
          {pendingLayaways.map(s => {
            const remaining = (s.price || 0) - (s.deposit_amount || 0);
            return (
              <div key={s.id} style={{ background: t.card, borderRadius: 12, padding: '12px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  {s.customer_name && <p style={{ fontSize: 15, fontWeight: 700, margin: 0, color: t.text }}>👤 {s.customer_name}</p>}
                  <p style={{ fontSize: 13, fontWeight: 500, margin: '2px 0 0' }}>{s.product_name}</p>
                  <p style={{ fontSize: 11, color: t.textSecondary, margin: '2px 0 0' }}>{s.folio} · {s.sale_date}</p>
                  <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 11, flexWrap: 'wrap' }}>
                    <span>Total: <strong>${(s.price || 0).toLocaleString()}</strong></span>
                    <span style={{ color: C.turquesa }}>Anticipo: <strong>${(s.deposit_amount || 0).toLocaleString()}</strong></span>
                    <span style={{ color: C.coral }}>Resta: <strong>${remaining.toLocaleString()}</strong></span>
                  </div>
                </div>
                <button onClick={() => completeLayaway(s)} style={{ background: C.turquesa, color: '#fff', border: 'none', borderRadius: 50, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: font }}>✓ Completar pago</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Today's sales */}
      <div style={{ background: t.card, border: `0.5px solid ${t.border}`, borderRadius: 20, padding: isMobile ? '1.25rem' : '1.5rem', marginBottom: 16 }}>
        <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 12px' }}>{salesType === 'rentas' ? 'Reservaciones de hoy' : `${unitLabel} de hoy`}</p>
        {todaySales.length === 0 ? (
          <p style={{ fontSize: 13, color: t.textSecondary, textAlign: 'center', padding: 12 }}>📝 Registra tu primera {unitLabel.toLowerCase().slice(0, -1)} del día</p>
        ) : (
          <>
            {todaySales.map(s => {
              const isLayaway = s.payment_status === 'layaway';
              const remaining = isLayaway ? (s.price || 0) - (s.deposit_amount || 0) : 0;
              return (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: `0.5px solid ${t.border}`, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12 }}>{isLayaway ? '🔒' : (getChannelLabel(s.channel, salesType).split(' ')[0])}</span>
                  <div style={{ flex: 1, minWidth: 100 }}>
                    <span style={{ fontSize: 13 }}>{s.customer_name ? `${s.customer_name} — ` : ''}{s.product_name}</span>
                    {s.folio && <span style={{ fontSize: 10, color: t.textSecondary, marginLeft: 6 }}>{s.folio}</span>}
                    {isLayaway && <div><span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: C.mango + '20', color: C.mango, fontWeight: 600 }}>Apartado — Resta ${remaining.toLocaleString()}</span></div>}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500, color: isLayaway ? C.mango : C.turquesa }}>${(s.price || 0).toLocaleString()}</span>
                  <button onClick={() => deleteSale(s.id)} style={{ background: 'none', border: 'none', color: t.textSecondary, fontSize: 14, cursor: 'pointer', opacity: 0.5 }}>×</button>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8 }}>
              <span style={{ fontSize: 12, color: t.textSecondary }}>Hoy: {todaySales.length}</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>${todayTotal.toLocaleString()}</span>
            </div>
          </>
        )}
      </div>

      {/* Charts — only if there are sales */}
      {sales.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
          {/* Dona: Digital vs Tradicional */}
          <div style={{ background: t.card, border: `0.5px solid ${t.border}`, borderRadius: 20, padding: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 14px', alignSelf: 'flex-start' }}>Internet vs {salesType === 'rentas' || salesType === 'servicios' ? 'Presencial' : salesType === 'boletos' ? 'Punto de venta' : 'Mostrador'}</p>
            <div style={{ width: 140, height: 140, borderRadius: '50%', background: `conic-gradient(${C.turquesa} 0deg ${digitalPct * 3.6}deg, ${C.coral} ${digitalPct * 3.6}deg 360deg)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 90, height: 90, borderRadius: '50%', background: t.card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 22, fontWeight: 600 }}>{digitalPct}%</span>
                <span style={{ fontSize: 10, color: t.textSecondary }}>digital</span>
              </div>
            </div>
            <p style={{ fontSize: 12, color: t.textSecondary, margin: '12px 0 0', textAlign: 'center' }}>
              {digitalPct >= 50 ? '🟢 ¡Tu negocio se está digitalizando!' : '🟡 Vamos a subir tus ventas digitales'}
            </p>
          </div>

          {/* By channel */}
          <div style={{ background: t.card, border: `0.5px solid ${t.border}`, borderRadius: 20, padding: '1.25rem' }}>
            <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 14px' }}>Por canal</p>
            {Object.entries(channelTotals).sort((a, b) => b[1] - a[1]).map(([ch, total]) => (
              <div key={ch} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12, width: 80 }}>{getChannelLabel(ch, salesType)}</span>
                <div style={{ flex: 1, height: 8, borderRadius: 4, background: t.isDark ? '#333' : '#eee', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${allTotal > 0 ? (total / allTotal) * 100 : 0}%`, background: CHANNEL_COLORS[ch] || C.turquesa, borderRadius: 4 }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: CHANNEL_COLORS[ch] || C.turquesa, minWidth: 60, textAlign: 'right' }}>${total.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top products */}
      {topProducts.length > 0 && (
        <div style={{ background: t.card, border: `0.5px solid ${t.border}`, borderRadius: 20, padding: '1.25rem', marginBottom: 16 }}>
          <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 12px' }}>Top {salesType === 'rentas' ? 'espacios' : salesType === 'boletos' ? 'eventos' : 'productos'}</p>
          {topProducts.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: `0.5px solid ${t.border}` }}>
              <span style={{ fontSize: 12, color: t.textSecondary, width: 16 }}>{i + 1}</span>
              <span style={{ flex: 1, fontSize: 13 }}>{p.name}</span>
              <span style={{ fontSize: 11, color: t.textSecondary }}>{p.count}x</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.turquesa }}>${p.total.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Export */}
      {sales.length > 0 && (
        <button onClick={exportCSV} style={{ background: 'none', border: `0.5px solid ${t.border}`, borderRadius: 50, padding: '10px 24px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: font, color: C.turquesa }}>
          📥 Descargar reporte Excel
        </button>
      )}

      {/* Receipt Modal */}
      {receiptSale && (
        <>
          <div onClick={() => setReceiptSale(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: isMobile ? '95%' : 'auto', maxWidth: 300, zIndex: 9999 }}>
            <div id="td-receipt" style={{ background: '#fff', borderRadius: 12, padding: '24px 20px', color: '#000', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                {profile?.business_logo_url ? <img src={profile.business_logo_url} alt="" style={{ height: 32, objectFit: 'contain', marginBottom: 4 }} /> : <p style={{ fontSize: 14, fontWeight: 700, margin: '0 0 2px' }}>T&D</p>}
                <p style={{ fontSize: 11, margin: 0 }}>{profile?.business_name || 'Thinker & Doer'}</p>
              </div>
              <div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
              {receiptSale.payment_status === 'layaway' && (
                <p style={{ textAlign: 'center', fontWeight: 700, fontSize: 14, margin: '0 0 4px', color: '#FDBE02' }}>🔒 APARTADO</p>
              )}
              {receiptSale.is_completion && (
                <p style={{ textAlign: 'center', fontWeight: 700, fontSize: 14, margin: '0 0 4px', color: '#5dc1b9' }}>✓ PAGADO COMPLETO</p>
              )}
              <p style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 13, textAlign: 'center' }}>Folio: {receiptSale.folio}</p>
              {receiptSale.original_folio && (
                <p style={{ margin: '0 0 2px', fontSize: 10, textAlign: 'center', color: '#666' }}>Folio original: {receiptSale.original_folio}</p>
              )}
              <p style={{ margin: '0 0 2px' }}>Fecha: {receiptSale.sale_date}</p>
              {receiptSale.time && <p style={{ margin: '0 0 2px' }}>Hora: {receiptSale.time}</p>}
              {receiptSale.customer_name && <p style={{ margin: '0 0 2px' }}>Cliente: <strong>{receiptSale.customer_name}</strong></p>}
              <div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
              <p style={{ margin: '0 0 2px' }}>Producto:</p>
              <p style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 13 }}>{receiptSale.product_name}</p>
              <p style={{ margin: '0 0 2px' }}>Total: <strong>${(receiptSale.price || 0).toLocaleString()}</strong></p>
              {receiptSale.payment_status === 'layaway' && (
                <>
                  <p style={{ margin: '0 0 2px' }}>Anticipo: <strong style={{ color: '#5dc1b9' }}>${(receiptSale.deposit_amount || 0).toLocaleString()}</strong></p>
                  <p style={{ margin: '0 0 2px' }}>Resta por pagar: <strong style={{ color: '#eb636b' }}>${((receiptSale.price || 0) - (receiptSale.deposit_amount || 0)).toLocaleString()}</strong></p>
                </>
              )}
              {receiptSale.is_completion && (
                <>
                  <p style={{ margin: '0 0 2px' }}>Anticipo previo: <strong>${(receiptSale.deposit_amount || 0).toLocaleString()}</strong></p>
                  <p style={{ margin: '0 0 2px' }}>Pago final: <strong style={{ color: '#5dc1b9' }}>${(receiptSale.final_payment || 0).toLocaleString()}</strong></p>
                </>
              )}
              <p style={{ margin: 0 }}>Canal: {getChannelLabel(receiptSale.channel, salesType)}</p>
              <div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
              <p style={{ textAlign: 'center', margin: 0, fontSize: 11 }}>{receiptSale.payment_status === 'layaway' ? '¡Gracias por tu apartado!' : '¡Gracias por tu compra!'}</p>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
              <button onClick={printReceipt} style={{ flex: 1, minWidth: 70, background: C.logoGrad, color: '#fff', border: 'none', borderRadius: 50, padding: '10px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: font }}>🖨 Imprimir</button>
              <button onClick={downloadPDF} style={{ flex: 1, minWidth: 70, background: C.turquesa, color: '#fff', border: 'none', borderRadius: 50, padding: '10px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: font }}>📄 PDF</button>
              <button onClick={shareTicket} style={{ flex: 1, minWidth: 70, background: '#25D366', color: '#fff', border: 'none', borderRadius: 50, padding: '10px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: font }}>📱 Compartir</button>
            </div>
            <button onClick={() => setReceiptSale(null)} style={{ width: '100%', marginTop: 6, background: 'none', border: 'none', color: '#999', fontSize: 12, cursor: 'pointer', fontFamily: font, padding: 6 }}>Cerrar</button>
          </div>
        </>
      )}
    </section>
  );
}
